// Боковое меню оболочки (README §1.2): рамка, ширина, полоса 56, шторка на
// телефоне и сквозной блок лимитов. Содержимое — Sidebar активного раздела
// из реестра (обычно ui/SideMenu).

import { Suspense, useRef } from 'react'
import { useAppStore } from '../store/appStore'
import { MOBILE_QUERY, useMediaQuery } from '../lib/useMediaQuery'
import { resolveSection } from '../sections/registry'
import type { SectionDef } from '../sections/types'
import { Icon } from '../ui/Icon'
import { LimitsBlock, RailLimits, useMskClock } from '../ui/LimitsBlock'
import ErrorBoundary from './ErrorBoundary'
import './shell.css'

/** Ширина шторки на телефоне: min(300, 100vw − 48). */
function drawerWidth(): number {
  return Math.min(300, window.innerWidth - 48)
}

export default function SideNav() {
  const mobile = useMediaQuery(MOBILE_QUERY)
  const side = useAppStore((s) => s.side)
  const sideW = useAppStore((s) => s.sideW)
  const mobSide = useAppStore((s) => s.mobSide)
  const section = useAppStore((s) => s.section)
  const setSideW = useAppStore((s) => s.setSideW)
  const resetSideW = useAppStore((s) => s.resetSideW)
  const expandSide = useAppStore((s) => s.expandSide)
  const setMobSide = useAppStore((s) => s.setMobSide)
  const setSection = useAppStore((s) => s.setSection)
  const def = resolveSection(section)
  const drag = useRef<{ sx: number; w0: number } | null>(null)

  const openAccount = () => {
    setSection('account')
    setMobSide(false)
  }

  if (mobile) {
    if (!mobSide) return null
    const width = drawerWidth()
    return (
      <>
        <div className="abx-scrim" onClick={() => setMobSide(false)} />
        <nav className={`abx-nav abx-nav--drawer${narrowClass(width)}`} style={{ width }}>
          <NavBody def={def} onMore={openAccount} />
        </nav>
      </>
    )
  }

  if (!side) return null
  const rail = sideW < 120

  const onDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { sx: event.clientX, w0: sideW }
    document.body.classList.add('abx-dragging-col')
  }
  const onMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d) return
    setSideW(d.w0 + event.clientX - d.sx)
  }
  const onUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return
    drag.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    document.body.classList.remove('abx-dragging-col')
    setSideW(useAppStore.getState().sideW, true)
  }

  return (
    <nav className={`abx-nav${rail ? ' abx-nav--rail' : ''}${rail ? '' : narrowClass(sideW)}`} style={{ width: sideW }}>
      {rail ? <Rail def={def} expand={expandSide} /> : <NavBody def={def} onMore={openAccount} />}
      <div
        className="abx-nav__edge"
        title="потяните, чтобы изменить ширину · двойной клик — по умолчанию"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onDoubleClick={resetSideW}
      />
    </nav>
  )
}

function narrowClass(width: number): string {
  if (width < 215) return ' is-narrow is-narrower'
  if (width < 230) return ' is-narrow'
  return ''
}

function NavBody({ def, onMore }: { def: SectionDef; onMore: () => void }) {
  const Sidebar = def.Sidebar
  return (
    <>
      <div className="abx-nav__scroll">
        <ErrorBoundary scope={`меню «${def.title}»`} compact key={def.id}>
          <Suspense fallback={null}>
            <Sidebar />
          </Suspense>
        </ErrorBoundary>
      </div>
      <LimitsBlock onMore={onMore} />
    </>
  )
}

/** Полоса 56: колонка значков 40×40. Любой значок разворачивает меню целиком. */
function Rail({ def, expand }: { def: SectionDef; expand: () => void }) {
  const clock = useMskClock()
  const Extra = def.RailExtra
  return (
    <div className="abx-rail">
      <button className="abx-rail__btn" title="развернуть меню" onClick={expand}>
        <Icon name="chevronRight" size={18} />
      </button>
      <RailSection key={def.id} def={def} expand={expand} />
      {Extra ? <Extra expand={expand} /> : null}
      <span className="abx-rail__gap" />
      <RailLimits onClick={expand} />
      <span className="abx-rail__clock">{clock}</span>
    </div>
  )
}

function RailSection({ def, expand }: { def: SectionDef; expand: () => void }) {
  const info = def.useRail?.() ?? {}
  return (
    <button className="abx-rail__sec" title={info.tip ?? def.title} onClick={expand}>
      <Icon name={def.icon} size={18} />
      {info.count ? <span className="abx-rail__count">{info.count}</span> : null}
    </button>
  )
}
